import React, { useContext, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { partial, isEmpty } from 'underscore';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SmartTextEditor } from '@bingads-webui-campaign-react/smart-component-text';
import {
  SiteConfigContext,
  useDebouncedDispatchSiteConfig,
  useEditorStatusContext,
  InstrumentationServiceContext,
  editorStatusLoadingWrapperForPromise,
  useSavedSiteConfig,
  useUserAccountCustomer,
  useMobileSupportEnabled,
  useIsPmaxLiteIntegration,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  getComponentById,
  DATA_SOURCE_KEY_MAPPING,
  getDataSourceByKey,
} from '@bingads-webui-campaign-react/smart-component-util';
import {
  SmartImageEditor,
  ImageOptionsKey,
  useExistingUploadedImage,
} from '@bingads-webui-campaign-react/smart-component-image';
import { useConfirmDialog } from '@bingads-webui-campaign-react/common-hooks';

import { dataProperties, businessNameMaxCount } from './data-config';
import { proxyReadHeaderComponentConfig } from './component-config-reader';
import { logoImageOptionsConfig } from './logo-image-options';

const getImageFromFacebookInfo = fbInfo => fbInfo.profilePicture;

const getStyles = () => ({
  logoEditor: {
    paddingBottom: '18px',
  },
  label: {
    fontSize: '16px',
  },
});

const componentName = 'HeaderEditor';

// Currently header editor is integrated in introduction editor
export const UnStyledSmartHeaderEditor = ({
  classes,
  i18n,
  highlightField,
  componentId,
}) => {
  const { siteConfig, dispatchSiteConfig } = useContext(SiteConfigContext);
  const logger = useContext(InstrumentationServiceContext);
  const { savedSiteConfig } = useSavedSiteConfig();
  const confirmDialogPromiseRef = useRef({ resolve: null, reject: null });
  const isInPmaxLiteWizard = useIsPmaxLiteIntegration();

  const {
    readAccountWithLog,
    updateAccountWithLog,
  } = useUserAccountCustomer({ postponeRead: true, componentName });
  const debouncedDispatchSiteConfig = useDebouncedDispatchSiteConfig();
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const isMobile = useMobileSupportEnabled();
  const logoImageId = getDataSourceByKey(siteConfig, DATA_SOURCE_KEY_MAPPING.LogoImageId);
  const componentConfig = getComponentById(siteConfig, componentId);
  const {
    businessName,
    defaultBusinessName,
    logo: { fileName: logoFileName },
  } = proxyReadHeaderComponentConfig(componentConfig, siteConfig, i18n);

  const {
    selectedImageOption,
    setSelectedImageOption,
    facebookImageInfo,
    setFacebookImageInfo,
    uploadedFileInfo,
    setUploadedFileInfo,
    enableFacebookImport,
  } = useExistingUploadedImage({
    imageId: logoImageId,
    fileName: logoFileName,
  });

  const onChange = useCallback((property, { value }, enableDebounce = true) => {
    const update = {
      ...componentConfig,
      [property]: value,
    };

    const dispatch = enableDebounce ? debouncedDispatchSiteConfig : dispatchSiteConfig;
    dispatch({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: update,
    });
  }, [componentConfig, debouncedDispatchSiteConfig, dispatchSiteConfig]);

  const onNewLogoImageUploaded = (imageMetaData, fileObj) => {
    dispatchSiteConfig({
      type: 'UPDATE_IMAGES',
      payload: {
        imageIdsToRemove: logoImageId && [logoImageId],
        imagesToAdd: [imageMetaData],
      },
    });

    dispatchSiteConfig({
      type: 'UPDATE_DATASOURCE_BY_NAME',
      payload: {
        name: DATA_SOURCE_KEY_MAPPING.LogoImageId,
        value: imageMetaData.IdString,
      },
    });

    if (fileObj) { // used by logo editor
      setUploadedFileInfo({
        file: fileObj,
        image: imageMetaData,
      });
      onChange(dataProperties.logo, {
        value: {
          fileName: fileObj.name,
        },
      }, false);
    } else {
      setFacebookImageInfo(imageMetaData);
      onChange(dataProperties.logo, {
        value: null,
      }, false);
    }
  };

  const onDefaultLogoImageSelected = useCallback(() => {
    dispatchSiteConfig({
      type: 'UPDATE_DATASOURCE_BY_NAME',
      payload: {
        name: DATA_SOURCE_KEY_MAPPING.LogoImageId,
        value: null,
      },
    });
  }, [dispatchSiteConfig]);

  const onChoiceChange = (key) => {
    setSelectedImageOption(key);
    if (key === ImageOptionsKey.DEFAULT) {
      onDefaultLogoImageSelected();
    }
  };

  const updateAccountPromise = useCallback(
    () => readAccountWithLog({})
      .then((account) => {
        const updatedAccount = {
          ...account,
          BusinessAddress: {
            ...account.BusinessAddress,
            BusinessName: businessName,
          },
        };

        return updateAccountWithLog({ Account: updatedAccount });
      })
      .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
    , [businessName, dispatchEditorStatus, readAccountWithLog, updateAccountWithLog]
  );

  const onConfirm = useCallback(() => {
    logger.logTrace(`SmartPageEditor|${componentName}|ConfirmationDialog|ConfirmButton|clicked`);

    updateAccountPromise()
      .then(() => confirmDialogPromiseRef.current.resolve('User clicked update business name.'))
      .catch(err => confirmDialogPromiseRef.current.reject(err));
  }, [logger, updateAccountPromise]);

  const onCancel = useCallback(() => {
    confirmDialogPromiseRef.current.resolve('User clicked no update business name.');
    logger.logTrace(`SmartPageEditor|${componentName}|ConfirmationDialog|CancelButton|clicked`);
  }, [logger]);

  const [
    confirmUpdateAccountBusinessNameDialog,
    showDialog,
    isDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('You changed your Smart Pages\' business name')),
    content: i18n.getString(_TL_('Do you want to update your account\'s business name, too?')),
    confirmText: i18n.getString(_TL_('Yes')),
    onConfirm,
    cancelText: i18n.getString(_TL_('No thanks')),
    onCancel,
    className: 'smart-page-confirm-update-account-business-name-dialog',
    onDisplay: () => logger.logTrace(`SmartPageEditor|${componentName}|ConfirmationDialog|shown`),
    modalProps: { isBlocking: true },
  });

  const postUpdateBusinessName = useCallback(() => {
    const savedBusinessName =
      getDataSourceByKey(savedSiteConfig, DATA_SOURCE_KEY_MAPPING.BusinessName);

    // Bypass for expert account
    if (isInPmaxLiteWizard) {
      return Promise.resolve(null);
    }

    if (
      !isEmpty(businessName)
      && businessName.length <= businessNameMaxCount
      && businessName !== savedBusinessName
    ) {
      // Return a promise that resolve based on user interaction
      return new Promise((resolve, reject) => {
        confirmDialogPromiseRef.current.resolve = resolve;
        confirmDialogPromiseRef.current.reject = reject;

        showDialog();
      });
    }

    return Promise.resolve(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSiteConfig, businessName]);

  useEffect(() => {
    dispatchEditorStatus({
      type: 'SET_ON_EXIT_COMPONENT_EDITING_EFFECT',
      payload: postUpdateBusinessName,
    });
  }, [postUpdateBusinessName, dispatchEditorStatus]);

  return (
    <React.Fragment>
      {
        !isMobile
        && <h3 className={classes.label}>{i18n.getString(_TL_('Business name and logo'))}</h3>
      }
      <div data-editable-property={dataProperties.businessName}>
        <SmartTextEditor
          label={i18n.getString(_TL_('Business name'))}
          focus={highlightField === dataProperties.businessName}
          onChange={partial(onChange, dataProperties.businessName)}
          maxCharCount={businessNameMaxCount}
          dataSourceName={DATA_SOURCE_KEY_MAPPING.BusinessName}
          placeholder={defaultBusinessName}
          shouldTrimValue
          isRequired
        />
      </div>
      <SmartImageEditor
        componentId={componentId}
        i18n={i18n}
        className={classNames('smart-page-logo-editor', classes.logoEditor)}
        label={i18n.getString(_TL_('Business logo'))}
        getImageFromFacebookInfo={getImageFromFacebookInfo}
        imageOptionsConfig={logoImageOptionsConfig}
        onNewImageUploaded={onNewLogoImageUploaded}
        selectedKey={selectedImageOption}
        uploadedFileInfo={uploadedFileInfo}
        facebookImageInfo={facebookImageInfo}
        onChoiceChange={onChoiceChange}
        enableFacebookImport={enableFacebookImport}
      />
      {isDialogVisible && confirmUpdateAccountBusinessNameDialog}
    </React.Fragment>
  );
};

UnStyledSmartHeaderEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  highlightField: PropTypes.string,
  componentId: PropTypes.string.isRequired,
};

UnStyledSmartHeaderEditor.defaultProps = {
  highlightField: null,
};

export const SmartHeaderEditor = withDefaultStyles(
  UnStyledSmartHeaderEditor,
  getStyles
);
